import React from 'react';

import { Select } from '@aline/common';
import { useReactiveVar } from '@apollo/client/react';
import { Portal } from '@mui/material';
import { AlineButton, useVariablePanelFocusedField } from 'src/components';
import { useRequiredEditorRuntime } from 'src/editor/editor-runtime-registry';
import { mobileSheetAutoFocusVar } from 'src/editor/mobile/signing/mobile-sheet-autofocus';
import { mobileFinishSheetOpenVar } from 'src/editor/mobile/signing/MobileFinishSheet';
import { useWalkthroughActive, useWalkthroughCta } from 'src/editor/toolbars';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  button: {
    borderRadius: 999,
    boxShadow: theme.shadows[8],
    fontWeight: 600,
    /**
     * AlineButton's root locks height to 32px; unlock it so the pill can
     * breathe and hit a comfortable tap target on its own (no wrapper rail).
     */
    height: 'auto',
    minHeight: 44,
    minWidth: 96,
    padding: theme.spacing(1.25, 3),
  },
  container: {
    bottom: `calc(env(safe-area-inset-bottom, 0px) + ${theme.spacing(2)})`,
    position: 'fixed',
    right: theme.spacing(2),
    zIndex: theme.zIndex.modal - 1,
  },
}));

export function MobileWalkthroughNextButton(): JSX.Element | null {
  const { classes } = useStyles();
  const { tabId } = useRequiredEditorRuntime();
  const focusedField = useVariablePanelFocusedField(tabId);
  const { walkThroughActive } = useWalkthroughActive();
  const finishSheetOpen = useReactiveVar(mobileFinishSheetOpenVar);
  const { buttonText, disabled, handleClick, submitting } = useWalkthroughCta({
    finishLabel: 'Finish',
    /**
     * Present the "Are you sure?" sheet first so the user has a
     * confirmation step rather than committing on a single tap.
     */
    onConfirmFinishNeeded: () => mobileFinishSheetOpenVar(true),
  });

  if (!walkThroughActive) return null;
  if (focusedField || finishSheetOpen) return null;

  return (
    <Portal>
      <div className={classes.container}>
        <AlineButton
          buttonClassName={classes.button}
          color="primary"
          data-cy={Select.document.editor.walkthrough.advanceWalkthroughButton}
          /**
           * `disabled` reflects `fieldActionsPendingSave` from `useWalkthrough`
           * — block taps while the previous field save is still in flight.
           * Without this, a fast retap reads `orderedFieldsInCurrentDoc` as
           * locally-complete but `requesterCompletedAllFields` as
           * server-incomplete, and advance falls through to "No next document"
           * (stuck walkthrough). Surfaces under CI load on Mobile Chrome.
           */
          disabled={disabled}
          loading={submitting}
          variant="contained"
          onClick={event => {
            /**
             * Tapping the FAB to advance is a user gesture — auto-focus the
             * next field's input when its sheet opens.
             */
            mobileSheetAutoFocusVar(true);
            handleClick(event);
          }}
        >
          {buttonText}
        </AlineButton>
      </div>
    </Portal>
  );
}
